import { useState, useMemo } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal, ConfirmModal } from '../components/ui/Modal';
import { Avatar } from '../components/ui/Avatar';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatPercent, paginate, truncate } from '../utils/helpers';

// ─── v2.2 : les livreurs sont créés INDISPONIBLES par défaut ──────────────────
// Ils doivent activer eux-mêmes leur disponibilité via PUT /livreurs/availability
// après avoir changé leur mot de passe temporaire.

const DEFAULT_FORM = {
  name: '', email: '', phone: '',
  category: 'C', vehicle_brand: '', vehicle_model: '',
  vehicle_plate: '', vehicle_year: '', language: 'fr',
};

// Badge disponibilité v2.2 — distinction claire entre indisponible volontaire vs
// compte jamais activé (nouveau compte)
const AvailBadge = ({ livreur }) => {
  const isNew = livreur.total_deliveries_completed === 0 && !livreur.is_available;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: livreur.is_available ? 'var(--green)' : 'var(--text3)',
          boxShadow: livreur.is_available ? '0 0 6px var(--green)' : 'none',
        }} />
        <span style={{ fontSize: 12, color: livreur.is_available ? 'var(--green)' : 'var(--text3)', fontWeight: 600 }}>
          {livreur.is_available ? 'Disponible' : 'Indisponible'}
        </span>
      </div>
      {isNew && (
        <span style={{ fontSize: 10, color: 'var(--orange)', fontStyle: 'italic', paddingLeft: 14 }}>
          Compte non activé
        </span>
      )}
    </div>
  );
};

export const Livreurs = () => {
  const toast = useToast();

  const { data: raw, loading, error, refetch, setData } = useApi(
    () => adminApi.getLivreursList()
  );

  const [statsCache,  setStatsCache]  = useState({});
  const [loadingStats,setLoadingStats] = useState(false);
  const [search,      setSearch]      = useState('');
  const [catFilter,   setCatFilter]   = useState('all');
  const [availFilter, setAvailFilter] = useState('all');
  const [page,        setPage]        = useState(1);
  const [createModal, setCreateModal] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null); // { name, email, temporary_password }
  const [form,        setForm]        = useState(DEFAULT_FORM);
  const [saving,      setSaving]      = useState(false);
  const [quotaModal,  setQuotaModal]  = useState(null);
  const [quota,       setQuota]       = useState({ quota_min_daily: '', quota_max_daily: '' });
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting,    setDeleting]    = useState(false);
  const [permanent,   setPermanent]   = useState(false);
  const [detailModal, setDetailModal] = useState(null);

  const livreurs = raw?.data || [];

  // Compteurs pour l'alerte
  const unavailCount = livreurs.filter(l => !l.is_available).length;
  const newCount     = livreurs.filter(l => !l.is_available && (l.total_deliveries_completed ?? 0) === 0).length;

  const filtered = useMemo(() => livreurs.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || l.name?.toLowerCase().includes(q)
      || l.phone?.toLowerCase().includes(q)
      || l.vehicle_plate?.toLowerCase().includes(q);
    const matchCat   = catFilter   === 'all' || l.category === catFilter;
    const matchAvail = availFilter === 'all'
      || (availFilter === 'available' ?  l.is_available : !l.is_available);
    return matchSearch && matchCat && matchAvail;
  }), [livreurs, search, catFilter, availFilter]);

  const paged = paginate(filtered, page, 10);

  const openStats = async (livreur) => {
    setDetailModal(livreur);
    if (statsCache[livreur.livreur_id]) return;
    setLoadingStats(true);
    try {
      const res = await adminApi.getLivreurStats(livreur.livreur_id);
      setStatsCache(p => ({ ...p, [livreur.livreur_id]: res.data }));
    } catch {}
    setLoadingStats(false);
  };

  const handleCreate = async () => {
    if (!form.name || !form.phone) {
      toast.error('Nom et téléphone sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.createLivreur({
        ...form,
        vehicle_year: Number(form.vehicle_year) || null,
      });
      // v2.2 : le backend génère un mot de passe temporaire et l'envoie par email
      // Si la réponse contient le mot de passe, on l'affiche à l'admin
      const tmp = res.data?.temporary_password;
      setCreateModal(false);
      setForm(DEFAULT_FORM);
      refetch();
      if (tmp) {
        setCreatedInfo({
          name:  form.name,
          email: form.email,
          temporary_password: tmp,
        });
      } else {
        toast.success(`Livreur "${form.name}" créé ✓ — Email avec mot de passe temporaire envoyé à ${form.email}`);
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuota = async () => {
    if (!quota.quota_min_daily || !quota.quota_max_daily) {
      toast.error('Remplissez les deux quotas.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateLivreurQuotas(quotaModal.livreur_id, {
        quota_min_daily: Number(quota.quota_min_daily),
        quota_max_daily: Number(quota.quota_max_daily),
      });
      setData(prev => ({
        ...prev,
        data: prev.data.map(l =>
          l.livreur_id === quotaModal.livreur_id
            ? { ...l, quota_min_daily: Number(quota.quota_min_daily), quota_max_daily: Number(quota.quota_max_daily) }
            : l
        ),
      }));
      toast.success('Quotas mis à jour ✓');
      setQuotaModal(null);
    } catch (err) {
      toast.error(err.message || 'Erreur mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteLivreur(deleteModal.livreur_id, permanent);
      if (permanent) {
        setData(prev => ({ ...prev, data: prev.data.filter(l => l.livreur_id !== deleteModal.livreur_id) }));
        toast.success(`Livreur "${deleteModal.name}" supprimé définitivement.`);
      } else {
        setData(prev => ({
          ...prev,
          data: prev.data.map(l =>
            l.livreur_id === deleteModal.livreur_id ? { ...l, is_active: false, is_available: false } : l
          ),
        }));
        toast.success(`Livreur "${deleteModal.name}" désactivé. Les données sont préservées.`);
      }
      setDeleteModal(null);
      setPermanent(false);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Loading text="Chargement des livreurs..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      {/* Alerte v2.2 — livreurs indisponibles par défaut */}
      {unavailCount > 0 && (
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 20 }}>
          <span>ℹ️</span>
          <div>
            <strong>{unavailCount} livreur(s) indisponible(s)</strong>
            {newCount > 0 && <> dont <strong>{newCount} nouveau(x) compte(s)</strong> jamais activé(s)</>}.
            Les livreurs activent eux-mêmes leur disponibilité depuis l'application mobile
            (après avoir changé leur mot de passe temporaire).{' '}
            <span style={{ color: 'var(--text3)', fontSize: 11 }}>Aucune commande ne leur est assignée tant qu'ils sont indisponibles.</span>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Nom, téléphone, plaque..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="filter-select" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
          <option value="all">Catégorie: Toutes</option>
          <option value="A">A — Premium</option>
          <option value="B">B — Standard</option>
          <option value="C">C — Économique</option>
        </select>
        <select className="filter-select" value={availFilter} onChange={e => { setAvailFilter(e.target.value); setPage(1); }}>
          <option value="all">Disponibilité: Tous</option>
          <option value="available">Disponible ●</option>
          <option value="busy">Indisponible ○</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} livreurs</span>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
        <button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ Nouveau livreur</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Livreur</th><th>Catégorie</th><th>Véhicule</th>
                <th>Disponibilité</th><th>Note</th><th>Livraisons</th><th>Taux</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map(l => (
                <tr key={l.livreur_id}>
                  <td>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Avatar name={l.name} />
                      <div>
                        <div className="td-main">{l.name}</div>
                        <div className="td-sub">{l.phone}</div>
                        {l.email && <div className="td-sub">{l.email}</div>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge className={l.category === 'A' ? 'badge-green' : l.category === 'B' ? 'badge-blue' : 'badge-gray'}>
                      Cat. {l.category}
                    </Badge>
                  </td>
                  <td>
                    <div className="td-main">{truncate(`${l.vehicle_brand || ''} ${l.vehicle_model || ''}`.trim() || '—', 20)}</div>
                    <div className="td-sub">{l.vehicle_plate || '—'}</div>
                  </td>
                  <td>
                    <AvailBadge livreur={l} />
                  </td>
                  <td>⭐ {l.average_rating ?? '—'} <span style={{ color: 'var(--text3)', fontSize: 11 }}>({l.total_reviews ?? 0})</span></td>
                  <td>
                    <div>{l.total_deliveries_completed ?? l.total_deliveries ?? '—'}</div>
                    <div className="td-sub">{l.quota_current_daily ?? '—'} aujourd'hui</div>
                  </td>
                  <td>
                    <span style={{ fontWeight: 600, color: (l.total_rejections ?? 0) > 5 ? 'var(--red)' : 'var(--green)' }}>
                      {formatPercent(l.total_deliveries_completed ?? 0, (l.total_deliveries_completed ?? 0) + (l.total_rejections ?? 0) || 1)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openStats(l)}>Stats</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => {
                        setQuotaModal(l);
                        setQuota({ quota_min_daily: l.quota_min_daily ?? '', quota_max_daily: l.quota_max_daily ?? '' });
                      }}>Quotas</button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setDeleteModal(l)}
                        title="Supprimer ce livreur"
                      >🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {paged.items.length === 0 && (
            <EmptyState icon="🛵" title="Aucun livreur"
              text="Créez le premier livreur ou modifiez les filtres."
              action={<button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ Nouveau livreur</button>} />
          )}
        </div>
        {paged.total > 0 && <div style={{ padding: '0 16px 16px' }}><Pagination {...paged} onPage={setPage} /></div>}
      </div>

      {/* ── CREATE MODAL ────────────────────────────────────────────────────── */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Créer un livreur" size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCreateModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '+ Créer le livreur'}
          </button>
        </>}
      >
        {/* Note v2.2 */}
        <div className="inline-alert inline-alert-blue" style={{ marginBottom: 20 }}>
          <span>📧</span>
          <div style={{ fontSize: 12 }}>
            <strong>v2.2 :</strong> Un mot de passe temporaire sera généré automatiquement et envoyé par email.
            Le livreur sera créé <strong>indisponible</strong> — il activera lui-même sa disponibilité après
            avoir changé son mot de passe à la première connexion.
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Nom complet *</label>
            <input className="form-input" placeholder="Ahmed Tazi" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Catégorie *</label>
            <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
              <option value="A">A — Premium (clients A)</option>
              <option value="B">B — Standard (clients A & B)</option>
              <option value="C">C — Économique (clients B)</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email <span style={{ color: 'var(--red)' }}>*</span> <span style={{ color: 'var(--text3)', fontSize: 10 }}>(reçoit le mot de passe)</span></label>
            <input className="form-input" type="email" placeholder="livreur@mail.ma" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Téléphone *</label>
            <input className="form-input" placeholder="+212612..." value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Langue de l'email</label>
            <select className="form-select" value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}>
              <option value="fr">Français</option>
              <option value="ar">Arabe</option>
              <option value="en">Anglais</option>
            </select>
          </div>
        </div>
        <div className="divider" />
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--text2)' }}>🛵 Véhicule</div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Marque</label>
            <input className="form-input" placeholder="Honda" value={form.vehicle_brand} onChange={e => setForm(p => ({ ...p, vehicle_brand: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Modèle</label>
            <input className="form-input" placeholder="PCX 125" value={form.vehicle_model} onChange={e => setForm(p => ({ ...p, vehicle_model: e.target.value }))} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Plaque</label>
            <input className="form-input" placeholder="A-45678-23" value={form.vehicle_plate} onChange={e => setForm(p => ({ ...p, vehicle_plate: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Année</label>
            <input className="form-input" type="number" min="2000" max="2030" placeholder="2022" value={form.vehicle_year} onChange={e => setForm(p => ({ ...p, vehicle_year: e.target.value }))} />
          </div>
        </div>
      </Modal>

      {/* ── CRÉÉ AVEC SUCCÈS : afficher le mot de passe temporaire ─────────── */}
      <Modal open={!!createdInfo} onClose={() => setCreatedInfo(null)}
        title="✅ Livreur créé avec succès" size="sm"
        footer={<button className="btn btn-primary btn-full" onClick={() => setCreatedInfo(null)}>Fermer</button>}
      >
        {createdInfo && (
          <div>
            <div className="inline-alert inline-alert-green" style={{ marginBottom: 16 }}>
              <span>✅</span>
              <div>
                Le compte de <strong>{createdInfo.name}</strong> a été créé.
                Un email a été envoyé à <strong>{createdInfo.email}</strong>.
              </div>
            </div>
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                🔑 Mot de passe temporaire
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: 22, fontWeight: 800, letterSpacing: 3,
                color: 'var(--accent)', userSelect: 'all', cursor: 'text',
                textAlign: 'center', padding: '8px 0',
              }}>
                {createdInfo.temporary_password}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', marginTop: 6 }}>
                Cliquez pour sélectionner et copier
              </div>
            </div>
            <div className="inline-alert inline-alert-yellow">
              <span>⚠️</span>
              <div style={{ fontSize: 12 }}>
                Le livreur devra <strong>changer ce mot de passe</strong> lors de sa première connexion.
                Il sera <strong>indisponible</strong> jusqu'à ce qu'il active sa disponibilité lui-même dans l'app.
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── QUOTA MODAL ─────────────────────────────────────────────────────── */}
      <Modal open={!!quotaModal} onClose={() => setQuotaModal(null)} title={`Quotas — ${quotaModal?.name}`} size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setQuotaModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleUpdateQuota} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Mettre à jour'}
          </button>
        </>}
      >
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          Missions minimum et maximum par jour pour ce livreur.
        </p>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Quota min / jour</label>
            <input className="form-input" type="number" min="0" value={quota.quota_min_daily} onChange={e => setQuota(p => ({ ...p, quota_min_daily: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Quota max / jour</label>
            <input className="form-input" type="number" min="1" value={quota.quota_max_daily} onChange={e => setQuota(p => ({ ...p, quota_max_daily: e.target.value }))} />
          </div>
        </div>
      </Modal>

      {/* ── STATS MODAL ─────────────────────────────────────────────────────── */}
      <Modal open={!!detailModal} onClose={() => setDetailModal(null)} title={`Statistiques — ${detailModal?.name}`} size="md">
        {loadingStats
          ? <Loading text="Chargement des statistiques..." />
          : detailModal && (() => {
              const s = statsCache[detailModal.livreur_id];
              if (!s) return <div style={{ color: 'var(--text2)', fontSize: 13 }}>Statistiques non disponibles.</div>;
              return (
                <div>
                  {/* Disponibilité v2.2 */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: s.is_available ? 'rgba(74,240,160,0.07)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${s.is_available ? 'rgba(74,240,160,0.2)' : 'var(--border)'}`,
                    borderRadius: 10, padding: '10px 14px', marginBottom: 16,
                  }}>
                    <div style={{
                      width: 12, height: 12, borderRadius: '50%',
                      background: s.is_available ? 'var(--green)' : 'var(--text3)',
                    }} />
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 14, color: s.is_available ? 'var(--green)' : 'var(--text2)' }}>
                        {s.is_available ? 'Disponible' : 'Indisponible'}
                      </span>
                      {!s.is_available && (
                        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>
                          — Le livreur active sa disponibilité depuis l'app mobile
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid-2" style={{ gap: 12, marginBottom: 20 }}>
                    {[
                      { l: 'Collectes',     v: s.deliveries?.total_pickups },
                      { l: 'Livraisons',    v: s.deliveries?.total_deliveries },
                      { l: 'Refus',         v: s.deliveries?.total_rejections },
                      { l: 'Taux succès',   v: formatPercent(s.deliveries?.total_deliveries ?? 0, s.deliveries?.total_pickups || 1) },
                    ].map((k, i) => (
                      <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{k.l}</div>
                        <div style={{ fontWeight: 700, fontFamily: 'Syne', fontSize: 20 }}>{k.v ?? '—'}</div>
                      </div>
                    ))}
                  </div>
                  {[
                    { l: 'Catégorie',          v: `Catégorie ${s.category}` },
                    { l: 'Véhicule',           v: s.vehicle },
                    { l: 'Plaque',             v: s.vehicle_plate },
                    { l: 'Note moyenne',       v: `⭐ ${s.average_rating}/5 (${s.total_reviews} avis)` },
                    { l: 'Points perf.',       v: s.performance_points },
                    { l: 'Quota aujourd\'hui', v: `${s.quotas?.current_daily ?? '—'} / ${s.quotas?.max_daily ?? '—'}` },
                  ].map((r, i) => (
                    <div key={i} className="info-row">
                      <span className="info-label">{r.l}</span>
                      <span className="info-value">{r.v ?? '—'}</span>
                    </div>
                  ))}

                  {/* ── Statistiques courses v2.4 ── */}
                  {s.courses && (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, margin: '20px 0 10px' }}>
                        Statistiques courses
                      </div>
                      <div className="grid-2" style={{ gap: 10, marginBottom: 14 }}>
                        {[
                          { l: 'Total courses',   v: s.courses.total,          color: 'var(--accent)'  },
                          { l: 'Pickups',          v: s.courses.pickup,         color: 'var(--orange)'  },
                          { l: 'Deliveries',       v: s.courses.delivery,       color: 'var(--blue)'    },
                          { l: 'Distance totale',  v: `${s.courses.total_distance ?? 0} km`, color: 'var(--green)' },
                        ].map((k, i) => (
                          <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{k.l}</div>
                            <div style={{ fontWeight: 700, fontFamily: 'Syne', fontSize: 18, color: k.color }}>{k.v ?? '—'}</div>
                          </div>
                        ))}
                      </div>

                      {/* Temps moyens */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                        {[
                          { label: 'Durée moy. pickup',   val: s.courses.avg_duration_pickup,   max: 30, color: 'var(--orange)', best: s.courses.fastest_pickup },
                          { label: 'Durée moy. delivery', val: s.courses.avg_duration_delivery, max: 45, color: 'var(--blue)',   best: s.courses.fastest_delivery },
                        ].map((row, i) => {
                          const pct = row.val ? Math.min(100, Math.round((row.val / row.max) * 100)) : 0;
                          return (
                            <div key={i}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{row.label}</span>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: row.color }}>{row.val ? `${row.val} min` : '—'}</span>
                                  {row.best && <span style={{ fontSize: 11, color: 'var(--text3)' }}>⚡ record : {row.best} min</span>}
                                </div>
                              </div>
                              <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${pct}%`, background: row.color }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Distance moy. */}
                      {s.courses.avg_distance_per_course != null && (
                        <div className="info-row">
                          <span className="info-label">Dist. moy./course</span>
                          <span className="info-value">{s.courses.avg_distance_per_course} km</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* ── Performance QR codes v2.4 ── */}
                  {s.qr_scans && (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, margin: '20px 0 10px' }}>
                        Performance QR codes
                      </div>
                      {[
                        { label: 'QR1 scannés (Pickup client)',  val: s.qr_scans.qr1_scanned, total: s.courses?.pickup   || 0, color: 'var(--blue)'   },
                        { label: 'QR3 scannés (Pickup laverie)', val: s.qr_scans.qr3_scanned, total: s.courses?.delivery || 0, color: 'var(--orange)' },
                        { label: 'QR4 scannés (Delivery)',       val: s.qr_scans.qr4_scanned, total: s.courses?.delivery || 0, color: 'var(--green)'  },
                      ].map((qr, i) => {
                        const pct = qr.total > 0 ? Math.round((qr.val / qr.total) * 100) : 0;
                        return (
                          <div key={i} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                              <span style={{ fontSize: 12, color: 'var(--text2)' }}>{qr.label}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: qr.color }}>{qr.val} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>({pct}%)</span></span>
                            </div>
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${pct}%`, background: qr.color }} />
                            </div>
                          </div>
                        );
                      })}
                      {s.qr_scans.success_rate != null && (
                        <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px', textAlign: 'center', marginTop: 8 }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Taux de succès global</div>
                          <div style={{
                            fontWeight: 800, fontSize: 28, fontFamily: 'Syne',
                            color: s.qr_scans.success_rate >= 90 ? 'var(--green)' : s.qr_scans.success_rate >= 70 ? 'var(--orange)' : 'var(--red)',
                          }}>
                            {s.qr_scans.success_rate}%
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()
        }
      </Modal>

      {/* ── SUPPRESSION LIVREUR ── */}
      <Modal
        open={!!deleteModal}
        onClose={() => { setDeleteModal(null); setPermanent(false); }}
        title={`Supprimer — ${deleteModal?.name}`}
        size="sm"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setDeleteModal(null); setPermanent(false); }} disabled={deleting}>
              Annuler
            </button>
            <button
              className={`btn ${permanent ? 'btn-danger' : 'btn-secondary'}`}
              onClick={handleDelete}
              disabled={deleting}
              style={permanent ? {} : { borderColor: 'var(--orange)', color: 'var(--orange)', background: 'rgba(240,160,74,0.08)' }}
            >
              {deleting
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> En cours...</>
                : permanent ? '🗑 Supprimer définitivement' : '⏸ Désactiver'}
            </button>
          </>
        }
      >
        {deleteModal && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Choisissez le type de suppression pour <strong>{deleteModal.name}</strong> :
            </p>

            {/* Option soft */}
            <div
              onClick={() => setPermanent(false)}
              style={{
                border: `2px solid ${!permanent ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 10,
                cursor: 'pointer',
                background: !permanent ? 'rgba(232,240,74,0.05)' : 'var(--bg3)',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                <span style={{ fontSize: 20 }}>⏸</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: !permanent ? 'var(--accent)' : 'var(--text)' }}>
                    Désactivation (recommandé)
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Soft delete · Réversible</div>
                </div>
                {!permanent && <span style={{ marginLeft: 'auto', color: 'var(--accent)', fontWeight: 700 }}>✓</span>}
              </div>
              <ul style={{ fontSize: 12, color: 'var(--text2)', paddingLeft: 16, lineHeight: 1.8, margin: 0 }}>
                <li>Compte désactivé (<code>is_active = false</code>, <code>is_available = false</code>)</li>
                <li>Aucune mission ne lui sera assignée</li>
                <li>Toutes les données et l'historique sont préservés</li>
                <li>Réactivable à tout moment depuis la liste</li>
              </ul>
            </div>

            {/* Option hard */}
            <div
              onClick={() => setPermanent(true)}
              style={{
                border: `2px solid ${permanent ? 'var(--red)' : 'var(--border)'}`,
                borderRadius: 10,
                padding: '12px 14px',
                cursor: 'pointer',
                background: permanent ? 'rgba(240,74,122,0.05)' : 'var(--bg3)',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                <span style={{ fontSize: 20 }}>🗑</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: permanent ? 'var(--red)' : 'var(--text)' }}>
                    Suppression définitive
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Hard delete · IRRÉVERSIBLE</div>
                </div>
                {permanent && <span style={{ marginLeft: 'auto', color: 'var(--red)', fontWeight: 700 }}>✓</span>}
              </div>
              <ul style={{ fontSize: 12, color: 'var(--text2)', paddingLeft: 16, lineHeight: 1.8, margin: 0 }}>
                <li>Suppression complète de la base de données</li>
                <li>Livraisons, notes, assignations supprimées</li>
                <li>Commandes historiques anonymisées (préservées)</li>
                <li style={{ color: 'var(--red)', fontWeight: 600 }}>⚠️ Action irréversible — impossible à annuler</li>
              </ul>
            </div>

            {permanent && (
              <div className="inline-alert inline-alert-red" style={{ marginTop: 12 }}>
                <span>⚠️</span>
                <span>Vous êtes sur le point de <strong>supprimer définitivement</strong> ce livreur. Cette action ne peut pas être annulée.</span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};